/*
 * ResellerDocumentsBills is a component used for a tab in ResellerDocuments to show bill documents.
 */
import React, { Component } from 'react';
import { QueryRenderer } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Grid, Link, Paper, Typography } from '@mui/material';
import { Table, TableBody, TableCell, TableHead, TablePagination, TableSortLabel, TableRow, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ServerError from '../ui/ServerError';
import LoadingSpinner from '../ui/LoadingSpinner';
import SearchField from '../ui/SearchField';
import AppBindStyles from '../../AppBindStyles';
import { addDays, format, isValid, startOfDay, subMonths } from 'date-fns';

const abEnvironment = new AbEnvironment();

const ResellerDocumentsBillsQuery = graphql`
query ResellerDocumentsBillsQuery($accountId: UUID!, $invoiceNumber: String!, $startts: Datetime, $endts: Datetime, $orderDirection: Int, $first: Int, $offset: Int) {
  getdocumentbills(accountid: $accountId, invoicenumber: $invoiceNumber, startts: $startts, endts: $endts, orderdirection: $orderDirection, first: $first, offset: $offset) {
    totalCount
    nodes {
      invoiceId
      invoiceDate
      invoiceNumber
      invoiceDescription
    }
  }
}
`;

const styleSheet = theme => ({
  tblHead: {
    backgroundColor: '#f1f1f1',
  },
  tblHeadRow: {
    height: 24,
  },
  nonPaperContainer: {
    marginTop: 8
  }
});

class ResellerDocumentsBills extends Component {
  constructor(props) {
    super(props);
    
    let billToDate = new Date();
    let tDate = localStorage.getItem('invoiceToDate');
    if (tDate != null) {
      billToDate = new Date(tDate);
    }
    let searchToDateUtc = startOfDay(addDays(billToDate, 1)).toISOString();
    
    let billFromDate = new Date();
    let fDate = localStorage.getItem('invoiceFromDate');
    if (fDate != null) {
      billFromDate = new Date(fDate);
    } else {
      billFromDate = subMonths(billToDate, 1);
    }
    let searchFromDateUtc = startOfDay(billFromDate).toISOString();
    
    this.state = {
      billNumber: '',
      startDate: billFromDate,
      queryStartDateUtc: searchFromDateUtc,
      endDate: billToDate,
      queryEndDateUtc: searchToDateUtc,
      orderby: 1, /* For future use */
      orderdir: 0,
      page: 0,
      rowsPerPage: 10,
    };  
  }

  goTo(route) {
    this.props.history.push(`/${route}`);
  }

  setFromDate(theDate) {
    // Only set valid date
    if (isValid(theDate)) {
      let vdate = new Date(theDate);
      localStorage.setItem('invoiceFromDate', theDate);
      let searchDateUtc = startOfDay(vdate).toISOString()
      this.setState({ startDate: theDate, queryStartDateUtc: searchDateUtc, page : 0 });
    }
  }

  setToDate(theDate) {
    if (isValid(theDate)) {
      let vdate = new Date(theDate);
      localStorage.setItem('invoiceToDate', theDate);
      let searchDateUtc = startOfDay(addDays(vdate, 1)).toISOString()
      this.setState({ endDate: theDate, queryEndDateUtc: searchDateUtc, page : 0 });
    }
  }

  handleChange = name => event => {
    this.setState({
      [name]: event.target.value,
      page: 0,
    });
  };

  handleChangePage = (event, page) => {
    this.setState({page: page});
  }
  
  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page : 0});
  }

  setOrderBy = (ob, dir) => {
    let thedir = dir;
    if (dir === 'asc') {
      thedir = 0;
    } else if (dir === 'desc') {
      thedir = 1;
    }
    if (thedir !== 0 && thedir !== 1) {
      thedir = 0;
    }
    this.setState({orderby: ob, orderdir: thedir, page : 0});
  }

  toggleOrder = (ob) => {
    let newDir = 0;
    if (ob === this.state.orderby) {
        if (this.state.orderdir === 0) {
            newDir = 1;
        }
    }

    this.setState({orderby: ob, orderdir: newDir, page : 0});
  } 

  render() {
    const { logError } = this.props.auth;
    const classes = this.props.classes;

    // Get document term.
    let documentTerm = this.props.auth.getDocumentTerm('documentName');
    if (documentTerm === 'documentName') {
        documentTerm = 'Document';
    }
    let documentTermPlural = documentTerm + 's';
    
    // Get bill name.
    let billTerm = this.props.auth.getDocumentTerm('documentsInvoiceType_0_Name');
    if (billTerm === 'documentsInvoiceType_0_Name') {
        billTerm = 'Bill';
    }
    let billTermPlural = billTerm + 's';
    let billHeaderLabel = billTermPlural + ' are confirmations of payments you have made for subscriptions, services, and fees.';

    let billSearchLabel = 'Search by ' + billTerm.toLowerCase() + ' number';
    let billNumberSearch = '%';
    if (this.state.billNumber !== '')
    {
        billNumberSearch = '%' + this.state.billNumber + '%';
    }

    // Determine the offset
    let offset = 0;
    if (this.state.page > 0)
    {
      offset = this.state.page * this.state.rowsPerPage
    }
    let first = this.state.rowsPerPage;
  
    let totalCount = 0;

    let theDir = 'asc';
    if (this.state.orderdir === 1) {
        theDir = 'desc';
    }

    let tableDate = billTerm + ' date';
    let tableNumber = billTerm + ' number';

    let rowCounter = 1;

    return (
        <Grid container spacing={2} direction="row" justifyContent="flex-start" className={classes.nonPaperContainer}>
            <Grid item xs={12}>
                <Grid container spacing={2} direction="row" justifyContent="flex-start">
                    <Grid item xs={12}>
                        <Grid container spacing={0} direction="column" justifyContent="flex-start">
                            <Grid item>
                                <Typography variant="h6">{billTermPlural}</Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant="body2">{billHeaderLabel}</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <SearchField
                            id="invoiceNumber"
                            label={billSearchLabel}
                            value={this.state.billNumber}
                            onChange={this.handleChange('billNumber')}
                        />
                    </Grid>
                    <Grid item>
                        <div style={AppBindStyles.datePickerField}>
                            <DatePicker
                                maxDateMessage={AppBindStyles.datePickerMessageMax}
                                autoOk
                                label="From date"
                                value={this.state.startDate}
                                onChange={(date) => this.setFromDate(date) }
                                format="YYYY-MM-DD"
                                disableFuture
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </div>
                    </Grid>
                    <Grid item>
                        <div style={AppBindStyles.datePickerField}>
                            <DatePicker
                                autoOk
                                label="To date"
                                value={this.state.endDate}
                                onChange={(date) => this.setToDate(date) }
                                format="YYYY-MM-DD"
                                renderInput={(params) => <TextField {...params} />}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Grid>
            
            <Grid item xs={12}>
                <QueryRenderer
                  environment={abEnvironment.getEnvironment()}
                  query={ResellerDocumentsBillsQuery}
                  variables={{accountId: this.props.accountId,
                              invoiceNumber: billNumberSearch,
                              startts: this.state.queryStartDateUtc,
                              endts: this.state.queryEndDateUtc,
                              orderDirection: this.state.orderdir,
                              first: first,
                              offset: offset}}
                  render={({error, props})=> {
                    if (error) {
                        logError(error.message);
                        return (
                            <ServerError/>
                        );
                    } else if (props) {
                        // Determine offset information
                        totalCount = props.getdocumentbills.totalCount;
                        let theNodes = props.getdocumentbills.nodes;
                        return ( 
                            <React.Fragment>
                                <Paper>
                                    <Table>
                                        <TableHead className={classes.tblHead}>
                                            <TableRow className={classes.tblHeadRow}>
                                                <TableCell style={{width: 72}}>
                                                    <TableSortLabel
                                                        active={this.state.orderby === 1}
                                                        direction={theDir}
                                                        onClick={() => this.toggleOrder(1) }>
                                                        {tableDate}
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell style={{width: 72}}>
                                                    {tableNumber}
                                                </TableCell>
                                                <TableCell>
                                                    Description
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {theNodes.map(row => {
                                                let theHref = 'invoice?invoiceId=' + row.invoiceId + '&bt=' + encodeURIComponent(documentTermPlural);
                                                //date is unzoned time, add Z to have appropriate local time.
                                                let bDate = format(new Date(row.invoiceDate + 'Z'), 'yyyy-MM-dd');

                                                return (
                                                    <TableRow key={rowCounter++}>
                                                        <TableCell>
                                                            { bDate }
                                                        </TableCell>
                                                        <TableCell>
                                                            <Link variant="body2" onClick={this.goTo.bind(this, theHref)}>
                                                                {row.invoiceNumber}
                                                            </Link>
                                                        </TableCell>
                                                        <TableCell>
                                                            { row.invoiceDescription }
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </Paper>

                                <div style={AppBindStyles.paginationTableFooter}>
                                    <TablePagination
                                    rowsPerPageOptions={[10, 25, 50]}
                                    component="div"
                                    count={totalCount}
                                    rowsPerPage={this.state.rowsPerPage}
                                    page={this.state.page}
                                    backIconButtonProps={{
                                        'aria-label': 'Previous Page',
                                    }}
                                    nextIconButtonProps={{
                                        'aria-label': 'Next Page',
                                    }}
                                    onPageChange={this.handleChangePage}
                                    onRowsPerPageChange={this.handleChangeRowsPerPage}
                                    />                        
                                </div>
                            </React.Fragment>
                        );
                    }

                    return (
                    <div>
                        <Typography>Loading...</Typography>
                        <LoadingSpinner/>
                    </div>
                    );
                  }}
                />
            </Grid>
        </Grid>
    );
  }
}

ResellerDocumentsBills.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(ResellerDocumentsBills);